import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Check, ChevronLeft, Lightbulb, Loader2, Plus, X } from "lucide-react";
import { useBrandProfile } from "../hooks/useBrandProfile";
import { useAltaDeProducto } from "../hooks/useAltaDeProducto";
import { MAX_LARGO_SKU } from "../lib/detallesDelProducto";
import { BLOQUE_ESQUELETO } from "../components/EsqueletoSeccion";
import { useVolverEnLaBanda } from "../hooks/useVolverEnLaBanda";
import {
  BUENAS,
  CLAVES,
  FotoDeEjemplo,
  MALAS,
  type VolverDeConsejos,
} from "./ConsejosDeFotos";
import { useCuentaShell } from "./estilos/cuentaShell";
import { TiendaNubeIcon } from "../components/TiendaNubeIcon";
import { TempImageManager } from "../components/VariantForm";
import {
  AgregarVariosModal,
  type AccionesDeVarios,
} from "../components/AgregarVariosModal";
import {
  BotonCancelarDeAlta,
  BotonDeAlta,
  PieFijoDeAlta,
} from "../components/PieFijoDeAlta";
import { PreguntaDeSalidaDeCarga } from "../components/PreguntaDeSalidaDeCarga";
import {
  AvisoDelPdf,
  FotosYDatosDelPdf,
  useCargaDesdePdf,
  type CargaDesdePdf,
} from "../components/CargaDesdePdf";
import { ModalDeConsejos } from "../components/ModalDeConsejos";
import {
  useGuardiaDeSalida,
  type DestinoFrenado,
} from "./lanzamientos/redes/useGuardiaDeSalida";

/** A dónde vuelve la pantalla: Mi marca abre en Productos. */
const RUTA_MI_MARCA = "/mi-marca";
const RUTA_SUBIR = "/mi-marca/productos/nuevo";
const RUTA_CONSEJOS = "/mi-marca/productos/consejos";

type Modo = "uno" | "varios";

/** Ideas de vistas que ayudan (son ejemplos, no casilleros ni obligatorias). */
const VISTAS_SUGERIDAS = ["Frente", "Costado", "Atrás", "Detalle", "En uso"];

/** Las tarjetas blancas de arriba (nombre y vistas que suman). */
const TARJETA_DE_DATOS =
  "rounded-2xl bg-white px-4 py-3.5 shadow-[0_1px_2px_rgba(16,24,40,0.05),0_0_0_1px_rgba(16,24,40,0.06)] sm:px-5 sm:py-4";

/** Tres vistas del MISMO par de zapatillas: lo que se sube cuando es un producto. */
const FOTOS_UN_PRODUCTO = [
  "/images/multi-vistas/zapatillas/perspectiva.webp",
  "/images/multi-vistas/zapatillas/frente.webp",
  "/images/multi-vistas/zapatillas/lateral.webp",
];

/**
 * Seis productos DISTINTOS, todos de moda: lo que se sube cuando son varios. Jean,
 * zapatillas y remera son miniaturas de 400px de los placeholders de "Con modelo"
 * (`public/images/subir-productos/`): los originales pesan 1,5–2 MB cada uno.
 */
const FOTOS_VARIOS_PRODUCTOS = [
  "/images/multi-vistas/otros/cartera-nobg.png",
  "/images/subir-productos/jean.png",
  "/images/subir-productos/zapatillas.png",
  "/images/subir-productos/remera.png",
  "/images/multi-vistas/otros/P00892308-nobg.png",
  "/images/multi-vistas/otros/buzo-nobg.png",
];

const TEXTOS: Record<"elegir" | Modo, { titulo: string; bajada: string }> = {
  elegir: {
    titulo: "Subir productos",
    bajada: "¿Cómo querés empezar? Elegí una opción para cargar tus productos.",
  },
  uno: {
    titulo: "Subir un producto",
    bajada: "Cargá las fotos de un producto para empezar a crear contenido con él.",
  },
  varios: {
    titulo: "Subir varios productos",
    bajada: "Cargá muchos productos juntos para empezar a crear contenido con ellos.",
  },
};

/**
 * "Subir productos", una pantalla de Mi marca — ADR-2026-10-13-subir-productos-es-una-pantalla.
 *
 * Dos pasos (ADR-2026-10-13-subir-productos-elegir-con-dos-tarjetas):
 *   1. ELEGIR: sólo dos tarjetas grandes y con fotos, "Un solo producto" (tres vistas
 *      del mismo par de zapatillas) y "Varios productos a la vez" (seis productos de moda
 *      distintos). Nada más
 *      en la pantalla: hasta elegir no hay nada que cargar.
 *   2. CARGAR (`?modo=uno|varios`): lo mismo que tenía el panel de "Agregar producto". La
 *      zona de fotos con los ejemplos del rubro y el nombre (`TempImageManager` + el campo
 *      de `VariantForm`), o el alta en bloque con Tiendanube y Mercado Libre
 *      (`AgregarVariosModal` con `enPagina`).
 *
 * Elegir APILA el paso en el historial: el "atrás" del navegador vuelve a las tarjetas. Lo
 * cargado no se pierde al volver a elegir: el alta de un producto vive en el hook de esta
 * pantalla y el alta en bloque queda montada (escondida) desde que se abre por primera vez.
 */
export const SubirProductos = () => {
  const navigate = useNavigate();
  const { profile, currentBrandId, activeBrand, loading: cargandoMarca } = useBrandProfile();
  const companyId = profile?.id_company ?? null;
  const alta = useAltaDeProducto({ companyId, brandId: currentBrandId });
  // El rubro de la marca activa, con el de la empresa de respaldo (el orden de toda la
  // app). El del hook arranca en Moda por defecto: con él, mientras cargaba la marca se
  // veían los ejemplos de Moda y después saltaban a los del rubro real. Por eso el
  // contenido espera a `cargandoMarca` con un esqueleto (ver abajo).
  const rubro = activeBrand?.rubro ?? profile?.rubro ?? alta.rubro;

  const [searchParams] = useSearchParams();
  const param = searchParams.get("modo");
  const modo: Modo | null = param === "uno" || param === "varios" ? param : null;
  const [variosMontado, setVariosMontado] = useState(modo === "varios");
  useEffect(() => {
    if (modo === "varios") setVariosMontado(true);
  }, [modo]);
  const [variosOcupado, setVariosOcupado] = useState(false);
  // "Crear N productos" del alta en bloque, para la cabecera en escritorio
  // (ADR-2026-10-13-varios-productos-reestructurado).
  const [accionesVarios, setAccionesVarios] = useState<AccionesDeVarios | null>(null);
  // El PDF del proveedor soltado en la zona de fotos (ADR-2026-10-18-el-pdf-entra-por-la-subida-de-siempre):
  // la propuesta se vuelca en el MISMO alta; lo propio (más fotos del PDF y datos) vive en este hook.
  const pdf = useCargaDesdePdf(alta);
  const ocupado = alta.guardando || variosOcupado || pdf.fase === "leyendo";

  // Todos los cambios de paso van con el fundido del shell, como un cambio de sección de
  // la sidebar (ADR-2026-10-13-subir-productos-mismo-ancho-y-entrada-suave). El fundido
  // también mira el query, así que elegir una tarjeta se apaga y se enciende igual. Fuera
  // del shell de escritorio (el teléfono) `conFadeDeSalida` corre la acción tal cual.
  const { conFadeDeSalida } = useCuentaShell();
  const irAMiMarca = () => conFadeDeSalida(() => navigate(RUTA_MI_MARCA));
  const elegir = (m: Modo) =>
    conFadeDeSalida(() => navigate(`${RUTA_SUBIR}?modo=${m}`));
  const volverAElegir = () => conFadeDeSalida(() => navigate(RUTA_SUBIR));

  // ── Irse de "Varios" con productos cargados y sin crear ─────────────────────
  // ADR-2026-10-14-varios-pregunta-antes-de-salir. El alta en bloque no tiene borrador:
  // irse la pierde. Toda salida —el volver, "Cancelar", la sidebar, el atrás del navegador—
  // pregunta antes: crear y seguir, descartar y seguir, o quedarse. Cerrar o recargar la
  // pestaña muestra el cartel del navegador. La guardia es la del editor de posts
  // (`useGuardiaDeSalida`: la app usa `BrowserRouter`, sin `useBlocker`).
  const cargadoEnVarios = modo === "varios" && !!accionesVarios?.hayCargado;
  const [salida, setSalida] = useState<DestinoFrenado | null>(null);
  // Descartar remonta el alta en bloque (está montada aunque se vea otro paso).
  const [claveVarios, setClaveVarios] = useState(0);
  // Adónde seguir cuando termine de crear desde la pregunta.
  const destinoPendiente = useRef<DestinoFrenado | null>(null);
  const guardia = useGuardiaDeSalida(
    { preguntar: cargadoEnVarios, interceptar: cargadoEnVarios },
    (d) => setSalida(d),
  );
  /** Seguir hacia `d` con el fundido (y reemplazando la entrada de guardia si quedó). */
  const irA = (d: DestinoFrenado) => conFadeDeSalida(() => guardia.continuar(d));
  const descartarYSeguir = () => {
    if (!salida) return;
    const d = salida;
    setSalida(null);
    setClaveVarios((k) => k + 1);
    irA(d);
  };
  const crearYSeguir = () => {
    if (!salida || !accionesVarios) return;
    destinoPendiente.current = salida;
    accionesVarios.crear();
  };
  const quedarse = () => {
    if (salida) guardia.quedarse(salida);
    destinoPendiente.current = null;
    setSalida(null);
  };
  // Terminó de crear (desde la cabecera o desde la pregunta): a donde iba, o a Mi marca.
  const alTerminarVarios = () => {
    const d = destinoPendiente.current ?? { tipo: "ruta" as const, a: RUTA_MI_MARCA };
    destinoPendiente.current = null;
    setSalida(null);
    irA(d);
  };
  // Si crear desde la pregunta falla, el alta en bloque no termina: se cierra la pregunta
  // para que se vea el error, y se queda.
  const creandoAntes = useRef(false);
  useEffect(() => {
    const creando = !!accionesVarios?.creando;
    if (creandoAntes.current && !creando && salida) quedarse();
    creandoAntes.current = creando;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accionesVarios?.creando]);

  // Volver es un paso para atrás: de cargar a elegir, de elegir a Mi marca. Irse en medio
  // de un guardado desmonta la pantalla con las subidas en vuelo.
  const volver = () => {
    if (ocupado) return;
    if (cargadoEnVarios) return setSalida({ tipo: "ruta", a: RUTA_SUBIR });
    if (modo) volverAElegir();
    else irAMiMarca();
  };
  // "Cancelar" (en la barra de cargar) abandona la carga: tira el borrador y va directo a
  // Mi marca.
  const cancelar = () => {
    if (ocupado) return;
    if (cargadoEnVarios) return setSalida({ tipo: "ruta", a: RUTA_MI_MARCA });
    alta.descartar();
    pdf.descartar();
    irAMiMarca();
  };
  const destinoDeVolver = modo ? "Subir productos" : "Mi marca";
  // A la pantalla de consejos (buenas y malas fotos), con el mismo fundido —
  // ADR-2026-10-13-subir-productos-pantalla-de-consejos.
  const irAConsejos = () => conFadeDeSalida(() => navigate(RUTA_CONSEJOS));
  // Desde un solo producto, los consejos vuelven acá (y lo cargado sigue: el hook guarda
  // un borrador en memoria) — ADR-2026-10-13-un-solo-producto-reestructurado.
  // Desde varios, los consejos se abren en un pop-up: la carga no tiene borrador y navegar
  // la perdía (ADR-2026-10-14-consejos-en-un-pop-up-desde-varios).
  const [consejosAbiertos, setConsejosAbiertos] = useState(false);
  const irAConsejosDesdeUno = () =>
    conFadeDeSalida(() =>
      navigate(RUTA_CONSEJOS, {
        state: {
          volverA: `${RUTA_SUBIR}?modo=uno`,
          volverEtiqueta: TEXTOS.uno.titulo,
        } satisfies VolverDeConsejos,
      }),
    );

  // En el teléfono, el volver va en la banda flotante del cascarón.
  useVolverEnLaBanda(destinoDeVolver, volver);

  // Guardar pasa por el PDF: sin PDF es el guardado de siempre; con PDF, además, la ficha.
  const guardarUno = async () => {
    if (await pdf.guardar()) irAMiMarca();
  };

  const textos = TEXTOS[modo ?? "elegir"];

  // El MISMO contenedor que Mi marca (`px-4 sm:px-8 max-w-content mx-auto`, adentro de la
  // columna del shell): el título cae donde estaba el logo de la marca y el contenido
  // ocupa el ancho de la grilla — ADR-2026-10-13-subir-productos-mismo-ancho-y-entrada-suave.
  //
  // Entrada escalonada: cabecera, tarjetas y contenido, 70ms entre uno y otro, encima
  // del fundido del shell. Cabecera y tarjetas suben 6px (`aparece-tras-esqueleto`); el
  // contenido de cargar entra sólo con opacidad porque adentro está la barra de Guardar,
  // que en el teléfono es `fixed`, y un `transform` en un ancestro la ataría a la
  // pantalla (ADR-2026-08-18-la-animacion-de-entrada-no-deja-transform).
  return (
    <div className="px-4 pt-2 sm:px-8 sm:pt-2 max-w-content mx-auto">
      <div>
        {/* Cabecera: volver (desde `md`; abajo lo pone el cascarón), título y bajada. La
            `key` la remonta al cambiar de paso, para que vuelva a entrar. */}
        <div
          key={modo ?? "elegir"}
          className="aparece-tras-esqueleto lg:flex lg:items-end lg:justify-between lg:gap-6"
        >
          <div className="min-w-0">
          <button
            type="button"
            onClick={volver}
            disabled={ocupado}
            className="mb-4 hidden h-9 items-center gap-1 rounded-full bg-black/[0.05] pl-2.5 pr-3.5 text-[14px] font-medium text-black/60 transition-colors hover:bg-black/[0.09] hover:text-[#16181D] disabled:cursor-not-allowed disabled:opacity-40 md:inline-flex"
          >
            <ChevronLeft className="h-4 w-4" />
            {destinoDeVolver}
          </button>
          <h1 className="text-[26px] font-bold leading-tight tracking-[-0.02em] text-[#16181D]">
            {textos.titulo}
          </h1>
          <p className="mt-1.5 text-[14px] leading-snug text-black/50">{textos.bajada}</p>
          </div>
          {/* Un solo producto, en escritorio: Cancelar y Guardar arriba a la derecha, a la
              altura del título — ADR-2026-10-13-un-solo-producto-acciones-arriba. Abajo
              quedaban lejos y debajo del pliegue. En el teléfono siguen en la barra fija de
              abajo (al alcance del pulgar; arriba competirían con el volver de la banda). */}
          {modo === "varios" && !cargandoMarca && accionesVarios && (
            <div className="hidden shrink-0 items-center gap-2 lg:flex">
              <BotonCancelarDeAlta onClick={cancelar} disabled={ocupado} />
              <BotonDeAlta
                onClick={accionesVarios.crear}
                disabled={!accionesVarios.puedeCrear}
              >
                {accionesVarios.creando && <Loader2 className="h-4 w-4 animate-spin" />}
                {accionesVarios.etiqueta}
              </BotonDeAlta>
            </div>
          )}
          {modo === "uno" && !cargandoMarca && (
            <div className="hidden shrink-0 items-center gap-2 lg:flex">
              <BotonCancelarDeAlta onClick={cancelar} disabled={ocupado} />
              <BotonDeAlta
                onClick={guardarUno}
                disabled={
                  alta.producto.images.length === 0 || alta.guardando || !companyId || pdf.fase === "leyendo"
                }
              >
                {alta.guardando && <Loader2 className="h-4 w-4 animate-spin" />}
                {alta.guardando ? "Guardando…" : "Guardar producto"}
              </BotonDeAlta>
            </div>
          )}
        </div>

        {/* Paso 1 — elegir: las dos tarjetas y, debajo, la de consejos. */}
        {!modo && (
          <>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
            <TarjetaDeSubida
              titulo={TEXTOS.uno.titulo}
              detalle="Subí varias vistas del mismo producto. Más vistas, mejores resultados."
              onClick={() => elegir("uno")}
              retraso={70}
              visual={(abierta) => <VistasDeUnProducto abierta={abierta} />}
            />
            <TarjetaDeSubida
              titulo={TEXTOS.varios.titulo}
              detalle="Cada foto suma uno nuevo a tu catálogo. También podés importarlos desde tu tienda."
              onClick={() => elegir("varios")}
              retraso={140}
              visual={(abierta) => <MosaicoDeProductos abierta={abierta} />}
            />
          </div>
          <TarjetaDeConsejos onClick={irAConsejos} retraso={210} />
          </>
        )}

        {/* Paso 2 — cargar. Hasta saber la marca activa, esqueleto: los ejemplos dependen
            de su rubro y el producto se guarda con su `brand_id` (sin ella quedaría sin
            marca). */}
        {modo && cargandoMarca && (
          <div
            className="mt-6 space-y-5 motion-safe:animate-[aparecer-suave_0.45s_ease-out_both]"
            style={{ animationDelay: "70ms" }}
            aria-hidden
          >
            <div className={`h-[196px] !rounded-3xl ${BLOQUE_ESQUELETO}`} />
            <div className={`h-4 w-32 ${BLOQUE_ESQUELETO}`} />
            <div className={`h-11 w-full !rounded-full ${BLOQUE_ESQUELETO}`} />
          </div>
        )}
        {/* Montado aunque se esté eligiendo (escondido), para no perder lo cargado. Al
            volver a mostrarse, la animación de opacidad arranca de nuevo. */}
        <div
          className={`mt-6 motion-safe:animate-[aparecer-suave_0.45s_ease-out_both] ${!modo || cargandoMarca ? "hidden" : ""}`}
          style={{ animationDelay: "70ms" }}
        >
          <div className={modo === "uno" ? "" : "hidden"}>
            {/* Un solo producto — ADR-2026-10-13-un-solo-producto-reestructurado y
                ADR-2026-10-13-un-solo-producto-tarjetas-arriba. Desde `md`: arriba, una al lado
                de la otra, el nombre y qué vistas suman; debajo, a todo el ancho, las fotos
                (zona de subida o grilla de lo cargado); abajo de todo, los consejos
                resumidos. En el teléfono las FOTOS van primero: son la tarea, y con las dos
                tarjetas arriba la zona de subida quedaba abajo del pliegue
                (ADR-2026-10-13-un-solo-producto-en-mobile). */}
            <CuerpoDeUnProducto alta={alta} rubro={rubro} pdf={pdf} />

            <ResumenDeConsejos onVerTodos={irAConsejosDesdeUno} />

            <PieFijoDeAlta
              hastaLg
              arriba={
                alta.guardando ? (
                  <p className="pb-2 text-[13px] text-black/55">
                    Guardando el producto. Puede tardar unos segundos.
                  </p>
                ) : null
              }
            >
              <BotonCancelarDeAlta onClick={cancelar} disabled={ocupado} />
              <BotonDeAlta
                onClick={guardarUno}
                disabled={
                  alta.producto.images.length === 0 || alta.guardando || !companyId || pdf.fase === "leyendo"
                }
              >
                {alta.guardando && <Loader2 className="h-4 w-4 animate-spin" />}
                {alta.guardando ? "Guardando…" : "Guardar producto"}
              </BotonDeAlta>
            </PieFijoDeAlta>
          </div>

          {variosMontado && !cargandoMarca && (
            <div className={modo === "varios" ? "" : "hidden"}>
              <AgregarVariosModal
                key={claveVarios}
                open
                enPagina
                companyId={companyId}
                brandId={currentBrandId}
                userPlan={alta.plan}
                onClose={cancelar}
                // La grilla de Mi marca trae lo nuevo al volver (y sigue a los que
                // todavía se están procesando): acá no hay nada que refrescar.
                onSaved={() => {}}
                onOcupado={setVariosOcupado}
                onTerminado={alTerminarVarios}
                onAcciones={setAccionesVarios}
                // El resumen de consejos, igual que en "Un solo producto". Sin "Ver todos"
                // si ya hay algo cargado: el alta en bloque no tiene borrador y se perdería.
                alFinal={
                  <ResumenDeConsejos onVerTodos={() => setConsejosAbiertos(true)} />
                }
              />
            </div>
          )}
        </div>
      </div>
      <ModalDeConsejos abierto={consejosAbiertos} onCerrar={() => setConsejosAbiertos(false)} />
      {salida && (
        <PreguntaDeSalidaDeCarga
          etiquetaDeCrear={accionesVarios?.etiqueta ?? "Crear productos"}
          puedeCrear={!!accionesVarios?.puedeCrear}
          creando={!!accionesVarios?.creando}
          onCrear={crearYSeguir}
          onDescartar={descartarYSeguir}
          onQuedarse={quedarse}
        />
      )}
    </div>
  );
};

/**
 * El cuerpo de "Un solo producto": arriba el nombre y los ángulos que ayudan, debajo las
 * fotos. Lo usan la pantalla de Mi marca y el pop-up de subir de los modos
 * (`ModalDeSubirProductos` — ADR-2026-10-14-subir-productos-en-un-pop-up-desde-modelo),
 * así las dos son la misma cosa en dos envases. Quien lo monta pone los botones.
 */
export const CuerpoDeUnProducto: React.FC<{
  alta: ReturnType<typeof useAltaDeProducto>;
  rubro: string;
  /** En el pop-up (980 de ancho): chips más angostos, para que entren en una fila. */
  enVentana?: boolean;
  /**
   * El PDF del proveedor (ADR-2026-10-18-el-pdf-entra-por-la-subida-de-siempre): si está, la zona
   * de fotos también acepta el PDF, arriba se avisa y abajo van las demás fotos y los datos.
   */
  pdf?: CargaDesdePdf;
}> = ({ alta, rubro, enVentana = false, pdf }) => {
  // "Añadir SKU": un link gris en lugar de la aclaración del nombre; tocarlo abre el campo debajo
  // del nombre (ADR-2026-10-22-el-alta-tiene-anadir-sku). Si el borrador ya trae uno, abierto.
  const [conSku, setConSku] = useState(() => !!alta.producto.sku);
  return (
    <div className="flex flex-col gap-5 md:gap-6">
    {pdf && pdf.fase !== "nada" && (
      <div className="order-first">
        <AvisoDelPdf carga={pdf} />
      </div>
    )}
    <div className="order-2 grid grid-cols-1 gap-4 md:order-1 md:grid-cols-2 md:gap-5">
      <div className={TARJETA_DE_DATOS}>
        <div className="mb-3 flex items-baseline justify-between">
          <label
            htmlFor="subir-producto-nombre"
            className="text-[14px] font-semibold text-[#16181D]"
          >
            Nombre
          </label>
          <span className="text-[12.5px] text-black/40">Opcional</span>
        </div>
        <input
          id="subir-producto-nombre"
          type="text"
          value={alta.producto.nombre}
          onChange={(e) =>
            alta.setProducto({ ...alta.producto, nombre: e.target.value })
          }
          disabled={alta.guardando}
          placeholder="Ej: Zapatillas urbanas"
          className="h-[var(--btn-h)] w-full rounded-full border border-black/[0.08] bg-white px-4 text-[length:var(--text-body)] text-[#16181D] shadow-[0_1px_2px_rgba(16,24,40,0.04)] outline-none transition-shadow placeholder:text-black/35 focus:border-brand-primary focus:ring-4 focus:ring-[rgb(var(--brand-primary-rgb)_/_0.085)]"
        />
        {conSku ? (
          // Se abre con `desplegar-campo` (index.css). El hijo recorta mientras crece; el `p-1 -m-1`
          // le deja lugar al anillo de foco para que no quede cortado.
          <div className="mt-3 grid motion-safe:animate-[desplegar-campo_0.28s_ease-out_both]">
          <div className="-m-1 min-h-0 overflow-hidden p-1 pt-2">
            <div className="mb-2 flex items-baseline justify-between">
              <label
                htmlFor="subir-producto-sku"
                className="text-[14px] font-semibold text-[#16181D]"
              >
                SKU
              </label>
              <span className="text-[12.5px] text-black/40">Opcional</span>
            </div>
            <input
              id="subir-producto-sku"
              type="text"
              autoFocus={!alta.producto.sku}
              value={alta.producto.sku ?? ""}
              maxLength={MAX_LARGO_SKU}
              onChange={(e) =>
                alta.setProducto({ ...alta.producto, sku: e.target.value })
              }
              disabled={alta.guardando}
              placeholder="El código con el que lo identificás"
              autoComplete="off"
              spellCheck={false}
              className="h-[var(--btn-h)] w-full rounded-full border border-black/[0.08] bg-white px-4 text-[length:var(--text-body)] text-[#16181D] shadow-[0_1px_2px_rgba(16,24,40,0.04)] outline-none transition-shadow placeholder:text-black/35 focus:border-brand-primary focus:ring-4 focus:ring-[rgb(var(--brand-primary-rgb)_/_0.085)]"
            />
          </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConSku(true)}
            disabled={alta.guardando}
            className="mt-3 inline-flex items-center gap-1 pl-4 text-[12.5px] font-medium text-black/40 transition-colors hover:text-black/65"
          >
            <Plus className="h-3.5 w-3.5" />
            Añadir SKU
          </button>
        )}
      </div>
      <div className={TARJETA_DE_DATOS}>
        {/* Sin contador: el tope de 5 sigue, pero no se muestra
            (ADR-2026-10-13-un-solo-producto-sin-contador). La aclaración va DEBAJO de
            los chips, para que se lean como ideas y no como casilleros a completar. */}
        {/* Mismo esqueleto que la tarjeta del nombre: título (mb-3), chips del alto
            del campo (`--btn-h`) y la aclaración debajo (mt-3), así las dos filas
            quedan alineadas. */}
        <p className="mb-3 text-[14px] font-semibold text-[#16181D]">
          Ángulos y detalles que ayudan
        </p>
        {/* En el teléfono, una sola fila que se desliza de costado (hasta el borde de
            la tarjeta): con 40px de alto, en dos renglones la tarjeta quedaba alta. */}
        <ul className="-mx-4 flex gap-2 overflow-x-auto px-4 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 [&::-webkit-scrollbar]:hidden">
          {VISTAS_SUGERIDAS.map((v) => (
            <li
              key={v}
              className={`inline-flex h-[var(--btn-h)] shrink-0 items-center rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.07)] ${enVentana ? "px-3.5" : "px-[18px]"} text-[13.5px] font-medium text-brand-primary`}
            >
              {v}
            </li>
          ))}
        </ul>
        <p className="mt-3 text-[12.5px] leading-snug text-black/45">
          Cuantas más vistas del producto subas, mejor lo entiende la IA.
        </p>
      </div>
    </div>
    <div className="order-1 md:order-2">
      <TempImageManager
        images={alta.producto.images}
        imageError={alta.error}
        onChange={(images) => alta.setProducto({ ...alta.producto, images })}
        rubro={rubro}
        ancho
        sinEjemplos
        sinContador
        alElegirPdf={pdf ? (f) => void pdf.leer(f) : undefined}
      />
    </div>
    {pdf && pdf.fase === "listo" && (
      <div className="order-3 flex flex-col gap-5 md:gap-6">
        <FotosYDatosDelPdf carga={pdf} alta={alta} />
      </div>
    )}
    </div>
  );
};

/**
 * Una de las dos formas de subir: una tarjeta grande, con las fotos arriba y el texto
 * abajo. Toda la tarjeta es el botón. `visual` recibe si está abierta (hover o foco) para
 * que las fotos reaccionen: las vistas del producto se abren en abanico y el mosaico se
 * levanta en ola.
 */
const TarjetaDeSubida: React.FC<{
  titulo: string;
  detalle: string;
  onClick: () => void;
  retraso: number;
  visual: (abierta: boolean) => React.ReactNode;
}> = ({ titulo, detalle, onClick, retraso, visual }) => {
  const [abierta, setAbierta] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setAbierta(true)}
      onMouseLeave={() => setAbierta(false)}
      onFocus={() => setAbierta(true)}
      onBlur={() => setAbierta(false)}
      className="aparece-tras-esqueleto group flex min-w-0 flex-col overflow-hidden rounded-[28px] bg-white text-left shadow-[0_1px_2px_rgba(16,24,40,0.05),0_0_0_1px_rgba(16,24,40,0.06)] transition-shadow duration-300 hover:shadow-[0_24px_48px_-20px_rgb(var(--brand-primary-rgb)_/_0.35),0_0_0_1.5px_var(--brand-primary)] focus-visible:outline-none focus-visible:shadow-[0_24px_48px_-20px_rgb(var(--brand-primary-rgb)_/_0.35),0_0_0_2px_var(--brand-primary)]"
      style={{ animationDelay: `${retraso}ms` }}
    >
      {/* Las fotos, sobre un degradé azulado con el halo de las tarjetas de Mi marca. */}
      <span className="relative block h-[230px] overflow-hidden bg-gradient-to-b from-[rgb(var(--brand-primary-rgb)_/_0.07)] to-[rgb(var(--brand-primary-rgb)_/_0.03)] sm:h-[300px] lg:h-[340px]">
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-2/3 bg-[radial-gradient(60%_70%_at_50%_0%,rgb(var(--brand-primary-rgb)_/_0.14),transparent)] opacity-70 transition-opacity duration-300 group-hover:opacity-100"
        />
        {/* Sin rótulo de cantidades ("1 producto · 3 fotos", "6 productos"): se leían
            como un dato de la cuenta y confundían. Las fotos ya lo dicen. */}
        {visual(abierta)}
      </span>
      <span className="flex items-center gap-4 px-5 py-4 sm:px-6 sm:py-5">
        <span className="min-w-0 flex-1">
          <span className="block text-[17px] font-semibold leading-tight text-[#16181D] sm:text-[19px]">
            {titulo}
          </span>
          <span className="mt-1 block text-[13.5px] leading-snug text-black/50">{detalle}</span>
        </span>
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.085)] text-brand-primary transition-colors duration-300 group-hover:bg-brand-primary group-hover:text-white">
          <ArrowRight className="h-[18px] w-[18px]" />
        </span>
      </span>
    </button>
  );
};

/**
 * Un producto: tres vistas del mismo par apiladas en abanico, la de frente adelante. Al
 * pasar el mouse el abanico se abre, como cuando se despliegan las fotos de algo.
 */
const VistasDeUnProducto: React.FC<{ abierta: boolean }> = ({ abierta }) => {
  // [desplazamiento horizontal en % del ancho de la foto, giro en grados] — cerrado / abierto.
  const poses: [number, number][] = abierta
    ? [[-92, -11], [0, 0], [92, 11]]
    : [[-68, -7], [0, 0], [68, 7]];
  return (
    <span aria-hidden className="absolute inset-0">
      {FOTOS_UN_PRODUCTO.map((src, i) => {
        const [x, giro] = poses[i];
        const centro = i === 1;
        return (
          <img
            key={src}
            src={src}
            alt=""
            loading="lazy"
            className="absolute left-1/2 top-1/2 aspect-[4/5] w-[28%] max-w-[180px] rounded-2xl object-cover shadow-[0_14px_30px_-12px_rgba(16,24,40,0.35)] ring-4 ring-white transition-transform duration-500 ease-[cubic-bezier(0.3,0.8,0.3,1)] motion-reduce:transition-none"
            style={{
              transform: `translate(-50%, -50%) translateX(${x}%) rotate(${giro}deg) scale(${
                centro ? (abierta ? 1.1 : 1.04) : 0.92
              })`,
              zIndex: centro ? 2 : 1,
            }}
          />
        );
      })}
    </span>
  );
};

/**
 * Varios productos: un mosaico de seis productos distintos. Al pasar el mouse se levantan
 * en ola (cada uno 40ms después del anterior).
 *
 * A los costados, montadas sobre el borde del mosaico, Tiendanube (izquierda, arriba, su
 * ícono de app: isotipo blanco sobre azul) y
 * Mercado Libre (derecha, abajo): la bajada dice que también se importan desde la tienda,
 * y los logos lo muestran en la misma imagen — ADR-2026-10-14-varios-con-logos-de-tiendas.
 * En hover se abren hacia afuera, como las vistas de la otra tarjeta.
 */
const MosaicoDeProductos: React.FC<{ abierta: boolean }> = ({ abierta }) => (
  <span
    aria-hidden
    className="absolute left-1/2 top-1/2 w-[70%] max-w-[370px] -translate-x-1/2 -translate-y-1/2 sm:w-[74%]"
  >
    <span className="grid grid-cols-3 gap-2.5 sm:gap-3">
      {FOTOS_VARIOS_PRODUCTOS.map((src, i) => (
        <img
          key={src}
          src={src}
          alt=""
          loading="lazy"
          className={`aspect-square w-full rounded-2xl bg-[#fafafa] shadow-[0_10px_24px_-12px_rgba(16,24,40,0.3)] ring-[3px] ring-white transition-transform duration-500 ease-[cubic-bezier(0.3,0.8,0.3,1)] motion-reduce:transition-none object-contain ${i === 0 ? "p-2" : i === 1 ? "p-2" : i === 5 ? "p-1" : ""}`}
          style={{
            transform: abierta ? "translateY(-6px) scale(1.03)" : "none",
            transitionDelay: `${i * 40}ms`,
          }}
        />
      ))}
    </span>
    <LogoDeTienda
      lado="izquierda"
      abierta={abierta}
      // El logo de app de Tiendanube: isotipo blanco sobre su azul (`#0150C3`, el de
      // `logos_plataformas/tiendanube.png`, sin la palabra: a este tamaño no se lee).
      fondo="bg-[#0150C3]"
      logo={<TiendaNubeIcon className="h-auto w-[60%] text-white" />}
    />
    <LogoDeTienda
      lado="derecha"
      abierta={abierta}
      logo={
        <img
          src="/images/mercado-libre.png"
          alt=""
          draggable={false}
          className="h-auto w-[74%] object-contain"
        />
      }
    />
  </span>
);

/**
 * Una ficha con el logo de una tienda, como un ícono de app, al costado del mosaico
 * de "Varios productos": asoma apenas sobre su borde (`translate` de 72% hacia afuera),
 * sin tapar los productos. Va por encima de las fotos (`z-10`).
 */
const LogoDeTienda: React.FC<{
  lado: "izquierda" | "derecha";
  abierta: boolean;
  logo: React.ReactNode;
  /** El fondo de la ficha; blanco si no se dice. */
  fondo?: string;
}> = ({ lado, abierta, logo, fondo = "bg-white" }) => {
  const izquierda = lado === "izquierda";
  const reposo = izquierda
    ? "translate(-72%, 0) rotate(-8deg)"
    : "translate(72%, 0) rotate(8deg)";
  const abierto = izquierda
    ? "translate(-92%, -6px) rotate(-12deg)"
    : "translate(92%, 6px) rotate(12deg)";
  return (
    <span
      className={`absolute z-10 flex aspect-square w-[17%] min-w-[48px] max-w-[84px] items-center justify-center rounded-[22%] ${fondo} shadow-[0_14px_28px_-12px_rgba(16,24,40,0.45)] ring-1 ring-black/[0.04] transition-transform duration-500 ease-[cubic-bezier(0.3,0.8,0.3,1)] motion-reduce:transition-none ${
        izquierda ? "left-0 top-[8%]" : "bottom-[8%] right-0"
      }`}
      style={{ transform: abierta ? abierto : reposo, transitionDelay: "60ms" }}
    >
      {logo}
    </span>
  );
};

/** Una foto que funciona y una que no, de las instrucciones del onboarding (moda). */
const EJEMPLOS_DE_CONSEJOS = [
  { src: "/images/onboarding/product/instructions/buena-entera.jpg", buena: true },
  { src: "/images/subir-productos/1.jpg", buena: false },
];

/**
 * Debajo de las dos formas de subir: una invitación a ver consejos para que las fotos
 * salgan bien — ADR-2026-10-13-subir-productos-tarjeta-de-consejos y
 * ADR-2026-10-13-subir-productos-consejos-destacada. Va a todo el ancho y más baja que las
 * de arriba (es ayuda opcional, no una tercera forma de subir), pero es el ÚNICO bloque de
 * color fuerte de la pantalla —el degradé del azul de marca— para que no pase de largo.
 * Las dos miniaturas (una foto buena con tilde, una mala con cruz) van giradas como fotos
 * sueltas y adelantan lo que hay adentro.
 *
 * El texto no repite palabras: el rótulo ubica el momento ("Antes de subir"), el título
 * habla de "fotos", la bajada de "imágenes" y "consejos" aparece sólo en el botón. La
 * flecha del botón no se mueve en hover, como en las de arriba.
 */
const TarjetaDeConsejos: React.FC<{ onClick: () => void; retraso: number }> = ({
  onClick,
  retraso,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="aparece-tras-esqueleto group relative mt-4 flex w-full flex-col gap-5 overflow-hidden rounded-[28px] bg-gradient-to-br from-brand-primary via-brand-primary-claro to-[rgb(var(--brand-primary-rgb)_/_0.65)] p-5 text-left text-white shadow-[0_18px_40px_-18px_rgb(var(--brand-primary-rgb)_/_0.65)] transition-shadow duration-300 hover:shadow-[0_26px_56px_-18px_rgb(var(--brand-primary-rgb)_/_0.8)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 sm:flex-row sm:items-center sm:gap-6 sm:px-6 sm:py-5 md:mt-5"
    style={{ animationDelay: `${retraso}ms` }}
  >
    {/* Brillos del fondo: que el azul no sea un plano. */}
    <span
      aria-hidden
      className="pointer-events-none absolute -left-10 -top-16 h-48 w-48 rounded-full bg-white/15 blur-3xl"
    />
    <span
      aria-hidden
      className="pointer-events-none absolute -bottom-20 right-24 h-48 w-64 rounded-full bg-white/[0.18] blur-3xl"
    />
    <span aria-hidden className="relative flex shrink-0 items-center pl-1 pt-1">
      {EJEMPLOS_DE_CONSEJOS.map((e, i) => (
        <span
          key={e.src}
          className={`relative block transition-transform duration-500 ease-[cubic-bezier(0.3,0.8,0.3,1)] motion-reduce:transition-none ${
            i === 0
              ? "-rotate-6 group-hover:-rotate-[9deg]"
              : "ml-3 rotate-6 group-hover:ml-4 group-hover:rotate-[9deg]"
          }`}
        >
          <img
            src={e.src}
            alt=""
            loading="lazy"
            className="h-[84px] w-[70px] rounded-xl object-cover shadow-[0_10px_22px_-8px_rgb(var(--brand-black-rgb)_/_0.55)] ring-[3px] ring-white sm:h-[96px] sm:w-[80px]"
          />
          <span
            className={`absolute -bottom-1.5 -right-1.5 flex h-6 w-6 items-center justify-center rounded-full text-white ring-2 ring-white ${
              e.buena ? "bg-success" : "bg-error"
            }`}
          >
            {e.buena ? (
              <Check className="h-3.5 w-3.5" strokeWidth={3} />
            ) : (
              <X className="h-3.5 w-3.5" strokeWidth={3} />
            )}
          </span>
        </span>
      ))}
    </span>
    <span className="relative min-w-0 flex-1">
      <span className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-[12px] font-semibold text-white ring-1 ring-inset ring-white/25">
        <Lightbulb className="h-3.5 w-3.5" />
        Antes de subir
      </span>
      <span className="block text-[18px] font-semibold leading-tight sm:text-[19px]">
        Sacale el máximo a tus fotos
      </span>
      <span className="mt-1 block text-[13.5px] leading-snug text-white/80">
        Te mostramos qué imágenes funcionan y cuáles conviene evitar para lograr los mejores
        resultados.
      </span>
    </span>
    <span className="relative inline-flex h-11 shrink-0 items-center justify-center gap-2 self-stretch rounded-full bg-white px-5 text-[14.5px] font-semibold text-brand-primary shadow-[0_6px_16px_-6px_rgb(var(--brand-black-rgb)_/_0.4)] transition-colors duration-300 group-hover:bg-[rgb(var(--brand-primary-rgb)_/_0.07)] sm:self-auto">
      Ver consejos
      <ArrowRight className="h-4 w-4" />
    </span>
  </button>
);

/**
 * Los consejos de "Cómo sacar buenas fotos", resumidos debajo de la carga de un solo
 * producto — ADR-2026-10-13-un-solo-producto-reestructurado y
 * ADR-2026-10-13-resumen-de-consejos-con-fotos. A la izquierda las cuatro claves (2×2, en
 * una línea cada una); a la derecha cuatro fotos de ejemplo en tamaño legible, dos que
 * funcionan y dos que no, con su marca y una palabra. "Ver todos los consejos" lleva a la
 * pantalla entera. Es ayuda: va abajo y no compite con la carga.
 */
export const ResumenDeConsejos: React.FC<{ onVerTodos?: () => void }> = ({ onVerTodos }) => (
  <section
    aria-label="Consejos para tus fotos"
    className="mt-8 rounded-2xl bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.05),0_0_0_1px_rgba(16,24,40,0.06)] sm:p-6"
  >
    <p className="flex items-center gap-1.5 text-[15px] font-semibold text-[#16181D]">
      <Lightbulb className="h-4 w-4 text-brand-primary" />
      Para mejores resultados
    </p>
    {/* Desde `lg` las claves van en UNA columna repartida a lo alto de las fotos
        (`content-between` sobre la fila estirada): en 2×2 centradas dejaban un hueco blanco
        arriba y abajo — ADR-2026-10-13-resumen-de-consejos-con-fotos-2. "Ver todos los
        consejos" va DEBAJO de las claves, con fondo celeste, y no arriba a la derecha como
        link suelto: cierra la lista y se destaca — ADR-2026-10-14-ver-consejos-debajo-de-las-claves.
        En el teléfono las claves no van (alargaban la pantalla): quedan las fotos y, debajo
        de ellas, "Ver todos los consejos". */}
    <div className="mt-5 grid grid-cols-1 gap-4 md:gap-6 lg:grid-cols-[minmax(0,0.75fr)_minmax(0,1.25fr)] lg:gap-10">
      <div className="order-2 flex flex-col md:order-none">
        <ul className="hidden grid-cols-2 md:grid gap-x-4 gap-y-4 lg:flex-1 lg:grid-cols-1 lg:content-between lg:gap-y-3">
          {CLAVES.map((c) => (
            <li key={c.titulo} className="flex min-w-0 items-start gap-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[rgb(var(--brand-primary-rgb)_/_0.07)] text-brand-primary [&>svg]:h-4 [&>svg]:w-4">
                {c.icono}
              </span>
              <span className="min-w-0">
                <span className="block text-[13.5px] font-semibold leading-tight text-[#16181D]">
                  {c.titulo}
                </span>
                <span className="mt-0.5 block text-[12.5px] leading-snug text-black/50">
                  {c.corto}
                </span>
              </span>
            </li>
          ))}
        </ul>
        {onVerTodos && (
          <button
            type="button"
            onClick={onVerTodos}
            className="inline-flex h-10 items-center justify-center gap-1.5 self-stretch md:mt-5 rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.07)] px-4 text-[13.5px] font-semibold text-brand-primary transition-colors hover:bg-[rgb(var(--brand-primary-rgb)_/_0.115)] sm:self-start"
          >
            Ver todos los consejos
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <ul className="grid grid-cols-4 gap-2.5 sm:gap-3">
        {[
          ...BUENAS.slice(0, 2).map((e) => ({ e, buena: true })),
          ...MALAS.slice(0, 2).map((e) => ({ e, buena: false })),
        ].map(({ e, buena }) => (
          <li key={e.titulo} className="min-w-0">
            <div className="relative aspect-[4/5] overflow-hidden rounded-xl bg-[#F1F1F1]">
              <FotoDeEjemplo foto={e.foto} />
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-black/[0.06]"
              />
              <span
                aria-label={buena ? "Funciona" : "Evitar"}
                className={`absolute bottom-1.5 left-1.5 flex h-5 w-5 items-center justify-center rounded-full text-white ring-2 ring-white sm:h-6 sm:w-6 ${
                  buena ? "bg-success" : "bg-error"
                }`}
              >
                {buena ? (
                  <Check className="h-3 w-3 sm:h-3.5 sm:w-3.5" strokeWidth={3} />
                ) : (
                  <X className="h-3 w-3 sm:h-3.5 sm:w-3.5" strokeWidth={3} />
                )}
              </span>
            </div>
            <p className="mt-1.5 truncate text-[12px] font-medium text-black/60 sm:text-[12.5px]">
              {e.etiqueta}
            </p>
          </li>
        ))}
      </ul>
    </div>
  </section>
);
